/**
 * Helpers puros (sin DI, testeables) del enlace de compartir del planner
 * embebido (HRS-2890): la web anfitriona pasa en el src del iframe un
 * `share_base` (URL de SU página del planner, ya resuelta con idioma y slug)
 * y el enlace compartido es `share_base` + `?shared_route=<token>`.
 */

/**
 * Valida un `share_base` recibido por query param contra la allowlist de hosts
 * del chatbot. Anti-phishing: sin esta validación, cualquiera que embeba
 * nuestro iframe podría hacer que el botón de compartir genere enlaces hacia
 * su propio dominio.
 *
 * Descarta (→ null): valor vacío, URL malformada, protocolo distinto de https,
 * credenciales embebidas (`https://user@host` pasaría un check de solo
 * hostname), query o fragmento presentes (contrato HRS-2890, comentario
 * 2026-08-03: el cliente se compromete a mandarla sin query ni fragmento, así
 * que su presencia se trata como manipulación) y host fuera de la allowlist —
 * match exacto de `URL.host` (nunca por sufijo: `endsWith` aceptaría
 * `visitmadrid.es.atacante.com`), que incluye el puerto
 * (`www.visitmadrid.es:8443` no valida) y ya viene lowercaseado. Allowlist
 * vacía → siempre null (fail closed).
 *
 * El valor llega YA decodificado (`URLSearchParams.get` decodifica el
 * URL-encoding del src): no aplicar `decodeURIComponent` extra, rompería un
 * `%` legítimamente codificado.
 */
export function sanitizeShareBase(raw: string | null | undefined, allowedHosts: readonly string[]): string | null {
  const value = raw?.trim();
  if (!value) return null;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;
  if (url.username || url.password) return null;
  if (url.search || url.hash) return null;
  if (!allowedHosts.includes(url.host)) return null;

  return url.toString();
}

/**
 * Añade `shared_route=<token>` a una URL base (el `share_base` validado o una
 * URL del mapa fallback). `searchParams.set` resuelve solo el dilema `?` vs `&`
 * y percent-encodea el token.
 */
export function buildSharedRouteLink(baseUrl: string, routeToken: string): string {
  const url = new URL(baseUrl);
  url.searchParams.set('shared_route', routeToken);
  return url.toString();
}
